import { supabase } from '@/integrations/supabase/client';

export interface ReAnalysisStatusService {
  resetReAnalysisStatus: (fileId: string) => Promise<{ success: boolean; affectedCount?: number; error?: string }>;
  clearPatternBackups: (fileId: string) => Promise<{ success: boolean; deletedCount?: number; error?: string }>;
  checkReAnalysisStatus: (botId: string) => Promise<{ success: boolean; completed: boolean; completedAt?: string; error?: string }>;
  updateReAnalysisStatus: (botId: string) => Promise<{ success: boolean; error?: string }>;
}

export const ReAnalysisService: ReAnalysisStatusService = {
  resetReAnalysisStatus: async (fileId: string) => {
    try {
      const { data, error } = await supabase.rpc('reset_re_analysis_status_for_file', {
        p_file_id: fileId
      });

      if (error) {
        console.error('Error resetting re-analysis status:', error);
        return { success: false, error: error.message };
      }

      return { success: true, affectedCount: data || 0 };
    } catch (error) {
      console.error('Error in resetReAnalysisStatus:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  },

  clearPatternBackups: async (fileId: string) => {
    try {
      const { data, error } = await supabase.rpc('clear_pattern_backups_for_file', {
        p_file_id: fileId
      });

      if (error) {
        console.error('Error clearing pattern backups:', error);
        return { success: false, error: error.message };
      }

      return { success: true, deletedCount: data || 0 };
    } catch (error) {
      console.error('Error in clearPatternBackups:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  },

  checkReAnalysisStatus: async (botId: string) => {
    try {
      const { data, error } = await supabase
        .from('bots')
        .select('re_analysis_completed_at')
        .eq('id', botId)
        .maybeSingle();

      if (error) {
        console.error('Error checking re-analysis status:', error);
        return { success: false, completed: false, error: error.message };
      }

      if (!data) {
        return { success: false, completed: false, error: 'Bot not found' };
      }

      return {
        success: true,
        completed: !!data.re_analysis_completed_at,
        completedAt: data.re_analysis_completed_at
      };
    } catch (error) {
      console.error('Error in checkReAnalysisStatus:', error);
      return { success: false, completed: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  },

  updateReAnalysisStatus: async (botId: string) => {
    try {
      const { error } = await supabase
        .from('bots')
        .update({ re_analysis_completed_at: new Date().toISOString() })
        .eq('id', botId);

      if (error) {
        console.error('Error updating re-analysis status:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in updateReAnalysisStatus:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }
};